import { nanoid } from "nanoid";

import { IsProd } from "@/constants/config";
import { MEMBERSHIP_CONFIG } from "@/constants/membership";
import { FlightItem } from "@/typings";
import { getTodayString } from "@/utils/index";
import request from "@/utils/request";

import { recordDeals, recordRefresh, recordRoutes } from "./record";
import { transformFlightData } from "./transformData";

const PATH_PREFIX = IsProd ? "/v1/" : "/vb/";
const FLIGHT_PATH_PREFIX = IsProd ? "/flight/" : "/flight-b/";

const getDate = (dateString: string) => {
  const dateParts = dateString.split("-");
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1; // Months are 0-indexed in JavaScript, so subtract 1
  const day = parseInt(dateParts[2], 10);

  const dateObject = new Date(year, month, day);
  return dateObject;
};

export const getGroupDatesV2 = (startDateStr: string, endDateStr: string, groupSize: number) => {
  const startDate = getDate(startDateStr);
  const endDate = getDate(endDateStr);

  const datesBetween = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");

    datesBetween.push(`${year}-${month}-${day}`);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const groups = [];
  let group = [];

  for (let i = 0; i < datesBetween.length; i++) {
    group.push(datesBetween[i]);
    if ((i + 1) % groupSize === 0 || i === datesBetween.length - 1) {
      groups.push(group);
      group = [];
    }
  }

  return groups;
};

export interface ApiGetRoutesParams {
  date_range: {
    from: string;
    to: string;
  };
  origins: {
    city: string[];
    region: string[];
    airports: string[];
  };
  arrivals: {
    city: string[];
    region: string[];
    airports: string[];
  };
}

export interface ApiRoutesItemSeats {
  f: number;
  w: number;
  y: number;
  j: number;
  ls: number;
}

export interface ApiRoutesItemPoints {
  f: number;
  ls: number;
  w: number;
  y: number;
  j: number;
  tax: {
    w?: number;
    y?: number;
    j?: number;
  };
  ss: ApiRoutesItemSeats | undefined;
  c_p: {
    f?: number;
    w?: number;
    y?: number;
    j?: number;
  };
}

export interface ApiRoutesItem {
  id: string;
  key: string;
  ls: number;
  d: string;
  date: string;
  r: string;
  route: string;
  pm: string;
  program: string;
  s_r: number;
  supported_route: number;
  p: ApiRoutesItemPoints;
  points: ApiRoutesItemPoints;
  pn: ApiRoutesItemPoints;
  points_ns: ApiRoutesItemPoints;
}

export const getFlightRoutes = (data?: ApiGetRoutesParams) => {
  return request.post<ApiGetRoutesParams, { data: ApiRoutesItem[] }>(PATH_PREFIX + "routes_data", {
    ...data,
  });
};

const field_mapping: any = {
  d: "date",
  p: "points",
  pn: "points_ns",
  pm: "program",
  r: "route",
  s_r: "supported_route",
};

export function mapField(item: any, addId = false) {
  Object.keys(item).map((key) => {
    // Check if key is in field_mapping
    if (field_mapping.hasOwnProperty(key)) {
      item[field_mapping[key]] = item[key];
      // Delete old key
      delete item[key];
    }
    if (addId) {
      item.hash = nanoid();
    }
  });
  item["key"] = item["date"] + item["program"] + item["route"];
}

export const getFlightRoutesV2 = (
  data: ApiGetRoutesParams,
  appendSearchData: any,
  setDialogOpen: any,
  setLoading: any,
  showUpsell: boolean
) => {
  // Record flight search
  recordRoutes({ query: JSON.stringify(data), type: "routes" });

  let search_data: any = [];
  const date_groups = getGroupDatesV2(data.date_range.from, data.date_range.to, 2);
  const path = PATH_PREFIX + "routes_data";

  const max_length = showUpsell
    ? MEMBERSHIP_CONFIG.routes_range[0]
    : MEMBERSHIP_CONFIG.routes_range[1];

  if (date_groups.length > max_length + 1) {
    setDialogOpen(true);
  }

  let count = Math.min(date_groups.length, max_length);

  for (let i = 0; i < Math.min(date_groups.length, max_length); i++) {
    const date_group = date_groups[i];
    const date_range = {
      from: date_group[0],
      to: date_group[date_group.length - 1],
    };
    const sub_data = {
      ...data,
      date_range,
    };
    request
      .post<ApiGetRoutesParams, { data: ApiRoutesItem[]; compressed_data: string }>(
        path + "-" + i,
        {
          ...sub_data,
        }
      )
      .then(async (res) => {
        // Map field
        await (res.data || []).map(async (item: any) => {
          await mapField(item, true);
        });
        // res.data = res.data.filter((item: any) => { return (item?.supported_route && item?.supported_route === 1) || true })

        // Append to search_data
        search_data = search_data.concat(res.data);
        // Sort by date string
        await search_data.sort((a: any, b: any) => {
          if (a.date && b.date) {
            return a.date.localeCompare(b.date);
          }
        });

        count -= 1;
        if (count <= 0) {
          appendSearchData(search_data, true);
        } else {
          appendSearchData(search_data);
        }
      })
      .catch((e) => {
        console.log("API ROUTE ERROR: ", e);
        count -= 1;
        if (count <= 1) {
          setLoading(false);
        }
      });
  }
};

export interface ApiGetFlightRoutesCalendarParams {
  id: string;
}
export const getFlightRoutesCalendar = (data: ApiGetFlightRoutesCalendarParams) => {
  return request.post<ApiGetFlightRoutesCalendarParams, { data: ApiRoutesItem[] }>(
    PATH_PREFIX + "routes_calendar_v2",
    {
      ...data,
    }
  );
};

export interface ApiGetFlightRoutesRefreshParams {
  pax: number;
  programs: string[];
  departure_airports: string[];
  arrival_airports: string[];
  date_range: {
    from: string;
    to: string;
  };
}

export interface ApiGetFlightRoutesSampleParams {
  program: string;
  origin: string;
  arrival: string;
  date: string;
}

export interface ApiFlightRoutesSampleData {
  route: string;
  data: {
    Economy: FlightItem[];
    "Premium Economy": FlightItem[];
    Business: FlightItem[];
    First: FlightItem[];
  };
  ls: number;
  data_ct: {
    Economy: number;
    "Premium Economy": number;
    Business: number;
    First: number;
  };
}
export const getFlightRoutesRefresh = async (data: ApiGetFlightRoutesRefreshParams) => {
  // console.log("%c [  ]-267", "font-size:13px; background:pink; color:#bf2c9f;", data);
  let date_from = data["date_range"]["from"];
  let date_to = data["date_range"]["to"];
  // If the date is in the foramt of YYYYMMDD. convert it to YYYY-MM-DD
  if (date_from.length === 8) {
    date_from = date_from.slice(0, 4) + "-" + date_from.slice(4, 6) + "-" + date_from.slice(6, 8);
    data["date_range"]["from"] = date_from;
  }
  if (date_to.length === 8) {
    date_to = date_to.slice(0, 4) + "-" + date_to.slice(4, 6) + "-" + date_to.slice(6, 8);
    data["date_range"]["to"] = date_to;
  }

  recordRefresh({ query: JSON.stringify(data), type: "refresh" });

  const res = await request.post<ApiGetFlightRoutesRefreshParams, ApiFlightRoutesSampleData>(
    "search_refresh",
    {
      ...data,
      raw_path: "search_refresh",
    }
  );

  return {
    ...res,
    data: {
      Economy: transformFlightData(res.data.data?.Economy),
      First: transformFlightData(res.data.data?.First),
      Business: transformFlightData(res.data.data?.Business),
      "Premium Economy": transformFlightData(res.data.data["Premium Economy"]),
    },
  };
};

export const getFlightRoutesSample = async (data: ApiGetFlightRoutesSampleParams) => {
  // Convert the date
  let date = data["date"];
  // If the date is in the foramt of YYYY-MM-DD. convert it to YYYYMMDD
  if (date.length === 10) {
    date = date.slice(0, 4) + date.slice(5, 7) + date.slice(8, 10);
    data["date"] = date;
  }

  const res = await request.post<ApiGetFlightRoutesSampleParams, ApiFlightRoutesSampleData>(
    PATH_PREFIX + "routes_sample",
    {
      ...data,
    }
  );

  return {
    ...res,
    data: {
      Economy: transformFlightData(res.data.Economy),
      First: transformFlightData(res.data.First),
      Business: transformFlightData(res.data.Business),
      "Premium Economy": transformFlightData(res.data["Premium Economy"]),
    },
  };
};

export interface ApiPostFlightRoutesVoteParams {
  program: string[];
  origin: string[];
  arrival: string[];
}
export const postFlightRoutesVote = (data: ApiPostFlightRoutesVoteParams) => {
  // Get local date in the format of YYYY-MM-DD
  const todayStr = getTodayString();
  return request.post<ApiPostFlightRoutesVoteParams, { success: boolean; message: string }>(
    PATH_PREFIX + "routes_vote",
    {
      ...data,
      date: todayStr,
    }
  );
};

export interface ApiFlightRoutesRecommendItem {
  date: string;
  target_cabin: string;
  route: string;
  price: number;
  tax: number;
  program: string;
  city_img: string;
  nonstop: boolean;
  stop_count: number;
  last_seen: number;
  ls: number;
  ss: number;
  c_p: number;
}
export const getFlightRoutesRecommend = (data: { origin: string[] }) => {
  return request.post<void, { data: ApiFlightRoutesRecommendItem[] }>(
    PATH_PREFIX + "routes_recommend",
    {
      ...data,
    }
  );
};

export const postRoutesRefresh = (data: { origin: string; arrival: string; program: string }) => {
  // Get local date in the format of YYYY-MM-DD
  const todayStr = getTodayString();
  return request.post<void, { message: string }>(PATH_PREFIX + "routes_refresh", {
    ...data,
    date: todayStr,
  });
};

export interface ApiAllRoutesItem {
  update_time: number;
  route: string;
  program: string;
}
export const getAllFlightRoutes = () => {
  return request.post<void, { res: ApiAllRoutesItem[] }>(PATH_PREFIX + "routes_all", {
    data: {},
  });
};

export type ApiDealsRoutesItem = ApiFlightRoutesRecommendItem;

export const getDealsFlightRoutes = (
  data: ApiGetRoutesParams,
  appendSearchData: any,
  setDialogOpen: any,
  setLoading: any,
  showUpsell: boolean
) => {
  // Record flight search
  recordDeals({ query: JSON.stringify(data), type: "first_class" });

  let search_data: any = [];
  const date_groups = getGroupDatesV2(data.date_range.from, data.date_range.to, 2);
  const path = PATH_PREFIX + "routes_deal";

  const max_length = showUpsell
    ? MEMBERSHIP_CONFIG.routes_range[0]
    : MEMBERSHIP_CONFIG.routes_range[1];

  if (date_groups.length > max_length + 1) {
    setDialogOpen(true);
  }

  let count = Math.min(date_groups.length, max_length);

  for (let i = 0; i < Math.min(date_groups.length, max_length); i++) {
    const date_group = date_groups[i];
    const date_range = {
      from: date_group[0],
      to: date_group[date_group.length - 1],
    };
    const sub_data = {
      ...data,
      date_range,
    };
    request
      .post<ApiGetRoutesParams, { data: ApiDealsRoutesItem[] }>(path + "-" + i, {
        ...sub_data,
      })
      .then(async (res) => {
        // // Map field
        // await (res.data || []).map(async (item: any) => {
        //   await mapField(item, true);
        // });

        // Append to search_data
        search_data = search_data.concat(res.data);
        // Sort by date string
        await search_data.sort((a: any, b: any) => {
          if (a.date && b.date) {
            return a.date.localeCompare(b.date);
          }
        });

        count -= 1;
        if (count <= 0) {
          appendSearchData(search_data, true);
        } else {
          appendSearchData(search_data);
        }
      })
      .catch((e) => {
        count -= 1;
        if (count <= 1) {
          setLoading(false);
        }
      });
  }
};
